import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/public/SEO";
import ContentBlocks from "@/components/public/ContentBlocks";
import ArticleCard from "@/components/public/ArticleCard";
import { Clock, ChevronRight } from "lucide-react";

export default function ArticleDetailPage({ section = "journal" }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(false);
      try {
        const { data } = await api.get(`/articles/${slug}?lang=tr`);
        setArticle(data);
      } catch (e) {
        if (e.response?.status === 301 && e.response?.data?.detail) {
          navigate(e.response.data.detail);
          return;
        }
        setErr(true);
      } finally { setLoading(false); }
    })();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (loading) return <div className="pt-40 max-w-3xl mx-auto px-6"><p className="text-cc-muted">Yükleniyor…</p></div>;
  if (err || !article) return (
    <div className="pt-40 max-w-3xl mx-auto px-6 text-center">
      <h1 className="font-serif text-4xl mb-4">Yazı bulunamadı</h1>
      <Link to="/" className="cc-link text-sm">Ana sayfaya dön</Link>
    </div>
  );

  const tr = article.translations?.tr || {};
  const cat = article.category?.translations?.tr;
  const breadcrumbs = [
    { label: "Ana Sayfa", url: "/" },
    section === "guide"
      ? { label: "Kapadokya Rehberi", url: "/kapadokya-rehberi" }
      : section === "encyclopedia"
      ? { label: "Kapadokya Ansiklopedisi", url: "/kapadokya-ansiklopedisi" }
      : { label: "Kapadokya Günlüğü", url: "/kapadokya-gunlugu" },
    { label: tr.title, url: window.location.pathname },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: tr.title,
    description: tr.meta_description,
    image: article.featured_image_url,
    datePublished: article.published_at,
    dateModified: article.updated_at,
    author: { "@type": "Organization", name: "Capella Cave" },
    publisher: { "@type": "Organization", name: "Capella Cave" },
    mainEntityOfPage: typeof window !== "undefined" ? window.location.href : "",
  };

  const faqSchema = (tr.faq && tr.faq.length) ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: tr.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <>
      <SEO
        title={tr.seo_title || tr.title}
        description={tr.meta_description || tr.excerpt}
        ogImage={tr.og_image_url || article.featured_image_url}
        ogType="article"
        publishedAt={article.published_at}
        modifiedAt={article.updated_at}
        author="Capella Cave"
        breadcrumbs={breadcrumbs}
        jsonLd={faqSchema ? [articleSchema, faqSchema] : articleSchema}
        noindex={article.noindex}
      />

      {/* Hero */}
      <section className="pt-40 pb-12 max-w-4xl mx-auto px-6 md:px-10" data-testid="article-header">
        <nav className="text-xs text-cc-muted mb-8 flex items-center gap-2" data-testid="article-breadcrumbs">
          {breadcrumbs.map((b, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <ChevronRight size={12} />}
              {i < breadcrumbs.length - 1
                ? <Link to={b.url} className="hover:text-cc-sepia">{b.label}</Link>
                : <span className="text-cc-text">{b.label}</span>}
            </span>
          ))}
        </nav>

        {cat && <div className="text-[11px] tracking-overline uppercase text-cc-sepia mb-5">{cat.name}</div>}
        <h1 className="font-serif text-4xl md:text-6xl text-cc-text leading-[1.05] mb-8">{tr.title}</h1>
        {tr.excerpt && <p className="text-cc-muted text-lg leading-relaxed max-w-2xl mb-6">{tr.excerpt}</p>}
        <div className="flex items-center gap-4 text-xs text-cc-faint">
          {article.reading_time && (
            <span className="flex items-center gap-1.5"><Clock size={12} /> {article.reading_time} dk okuma</span>
          )}
          {article.published_at && <span>{new Date(article.published_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</span>}
        </div>
      </section>

      {/* Hero image */}
      {article.featured_image_url && (
        <figure className="max-w-6xl mx-auto px-0 md:px-10 mb-16">
          <img src={article.featured_image_url} alt={tr.title} className="w-full aspect-[16/9] object-cover" />
        </figure>
      )}

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24" data-testid="article-body">
        <ContentBlocks blocks={tr.content_blocks || []} />

        {tr.faq && tr.faq.length > 0 && (
          <div className="mt-20 pt-12 border-t border-cc-border" data-testid="article-faq">
            <div className="text-[11px] tracking-overline uppercase text-cc-sepia mb-6">Sıkça Sorulanlar</div>
            <h2 className="font-serif text-3xl md:text-4xl text-cc-text mb-10">SSS</h2>
            <div className="space-y-8">
              {tr.faq.map((f, i) => (
                <div key={i} className="pb-8 border-b border-cc-border last:border-0">
                  <h3 className="font-serif text-xl text-cc-text mb-3">{f.q}</h3>
                  <p className="text-cc-muted leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Related */}
      {article.related && article.related.length > 0 && (
        <section className="bg-cc-bg py-24 border-t border-cc-border" data-testid="article-related">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-overline uppercase text-cc-sepia mb-3">Okumaya devam edin</div>
            <h2 className="font-serif text-3xl md:text-5xl text-cc-text mb-12">Benzer Yazılar</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
              {article.related.map((r, i) => <ArticleCard key={r.id} article={r} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
